import React, {useEffect, useState} from 'react';
import Modal from 'react-modal';
import Picture from '../../components/Picture';
import {updateCharacter, updateSheet} from '../characterRepository';
import {uploadPortrait, MAX_PORTRAIT_BYTES} from '../portraitRepository';
import {ADVANTAGE_KIND} from '../character';
import {DISCIPLINES_BY_ID, POWERS_BY_ID, sortDisciplineEntries, sortPowerIds} from '../data/disciplines';
import {localized} from '../data/traits';
import {vtmT, currentLanguage} from '../i18n';
import {TextField} from './creation/CreationParts';
import {ConvictionsStep} from './creation/IdentitySteps';
import '../../styles/vtm/roll.css';
import '../../styles/vtm/creation.css';

/** Édition libre (sans XP) des textes du personnage et de son portrait. */
const VtmProfileModal = ({isOpen, onClose, character}) => {
  const lng = currentLanguage();
  const [name, setName] = useState(character.name);
  const [sheet, setSheet] = useState(character.vtm);
  const [busy, setBusy] = useState(false);
  const [portraitState, setPortraitState] = useState('');

  useEffect(() => {
    if (isOpen) {
      setName(character.name);
      setSheet(character.vtm);
      setPortraitState('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const setAdvantage = (index, description) => setSheet({
    ...sheet,
    advantages: sheet.advantages.map((item, i) => (i === index ? {...item, description} : item)),
  });

  const setPowerNote = (powerId, note) => setSheet({...sheet, powerNotes: {...sheet.powerNotes, [powerId]: note}});

  // Même ordre que sur la fiche
  const ownedPowers = sortDisciplineEntries(sheet.disciplines, lng).flatMap(([disciplineId, discipline]) => sortPowerIds(discipline.powers || [], lng)
    .filter(powerId => POWERS_BY_ID[powerId])
    .map(powerId => ({powerId, disciplineId})));

  const changePortrait = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (file.size > MAX_PORTRAIT_BYTES) {
      setPortraitState('tooBig');
      return;
    }
    setPortraitState('uploading');
    try {
      const picture = await uploadPortrait(character.uid, file);
      await updateCharacter(character.uid, {picture});
      setPortraitState('');
    } catch (e) {
      console.log(e);
      setPortraitState('');
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      if (name.trim() && name !== character.name) await updateCharacter(character.uid, {name: name.trim()});
      await updateSheet(character.uid, {
        concept: sheet.concept,
        ambition: sheet.ambition,
        desire: sheet.desire,
        sire: sheet.sire,
        convictions: sheet.convictions.filter(item => (item.conviction || '').trim()),
        advantages: sheet.advantages,
        powerNotes: Object.fromEntries(Object.entries(sheet.powerNotes || {})
          .map(([powerId, note]) => [powerId, note.trim()])
          .filter(([, note]) => note)),
      });
      onClose();
    } catch (e) {
      console.log(e);
    }
    setBusy(false);
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className="vtmModal wide" overlayClassName="vtmModalOverlay" contentLabel={vtmT('editProfileTitle')}>
      <div className="vtmModalHeader">
        <h2 className="vtmModalTitle">{vtmT('editProfileTitle')}</h2>
      </div>
      <form className="vtmWizard vtmProfileForm" onSubmit={event => { event.preventDefault(); save(); }}>
        <div className="vtmProfilePortrait">
          <Picture character={character} />
          <div className="vtmField">
            <input id="vtmPortraitInput" className="vtmVisuallyHidden" type="file" accept="image/*" onChange={changePortrait} />
            <label className="vtmButton small vtmFileButton" htmlFor="vtmPortraitInput">{vtmT('changePortrait')}</label>
            {portraitState === 'uploading' && <span className="vtmHint" role="status">{vtmT('uploading')}</span>}
            {portraitState === 'tooBig' && <span className="vtmXpError" role="alert">{vtmT('portraitTooBig')}</span>}
          </div>
        </div>

        <div className="vtmStepFields">
          <TextField id="vtmEditName" label={vtmT('name')} value={name} onChange={setName} />
          <TextField id="vtmEditConcept" label={vtmT('concept')} value={sheet.concept} onChange={concept => setSheet({...sheet, concept})} />
          <TextField id="vtmEditAmbition" label={vtmT('ambition')} value={sheet.ambition} onChange={ambition => setSheet({...sheet, ambition})} />
          <TextField id="vtmEditDesire" label={vtmT('desire')} value={sheet.desire} onChange={desire => setSheet({...sheet, desire})} />
          <TextField id="vtmEditSire" label={vtmT('sire')} value={sheet.sire} onChange={sire => setSheet({...sheet, sire})} />
        </div>

        <h3 className="vtmCategoryTitle">{vtmT('convictions')}</h3>
        <ConvictionsStep sheet={sheet} change={setSheet} />

        {sheet.advantages.length > 0 && (
          <>
            <h3 className="vtmCategoryTitle">{vtmT('advantages')} · {vtmT('flaws')}</h3>
            <div className="vtmStepFields">
              {sheet.advantages.map((item, index) => (
                <TextField
                  key={index}
                  id={`vtmEditAdvantage${index}`}
                  label={`${item.kind === ADVANTAGE_KIND.FLAW ? vtmT('flaws') : vtmT('advantages')} · ${item.name} ${'●'.repeat(item.level)}`}
                  value={item.description}
                  placeholder={vtmT('advantageDescription')}
                  onChange={description => setAdvantage(index, description)}
                />
              ))}
            </div>
          </>
        )}

        {ownedPowers.length > 0 && (
          <>
            <h3 className="vtmCategoryTitle">{vtmT('powerNotes')}</h3>
            <p className="vtmHint">{vtmT('powerNotesHint')}</p>
            <div className="vtmStepFields">
              {ownedPowers.map(({powerId, disciplineId}) => (
                <TextField
                  key={powerId}
                  id={`vtmEditPowerNote-${powerId}`}
                  label={`${localized(DISCIPLINES_BY_ID[disciplineId]?.name, lng)} · ${localized(POWERS_BY_ID[powerId].name, lng)}`}
                  value={(sheet.powerNotes || {})[powerId]}
                  placeholder={vtmT('powerNotePlaceholder')}
                  onChange={note => setPowerNote(powerId, note)}
                  multiline
                />
              ))}
            </div>
          </>
        )}

        <div className="vtmModalActions">
          <button type="submit" className="vtmButton primary" disabled={busy}>{vtmT('save')}</button>
          <button type="button" className="vtmButton ghost" onClick={onClose}>{vtmT('cancel')}</button>
        </div>
      </form>
    </Modal>
  );
};

export default VtmProfileModal;
